import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Tag,
  message,
  Space,
  Popconfirm,
  Input,
  Select,
  DatePicker,
  Row,
  Col,
  Card,
  Tooltip,
  Dropdown,
  Menu,
  Modal,
} from 'antd';
import {
  DeleteOutlined,
  SyncOutlined,
  DollarOutlined,
  SearchOutlined,
  FilterOutlined,
  ClearOutlined,
  FileExcelOutlined,
  CalendarOutlined,
  PlusOutlined,
  EditOutlined,
  MoreOutlined,
  TransactionOutlined,
} from '@ant-design/icons';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { TransactionResponse } from '../../model/Transaction.model';
import TransactionService from '../../service/transaction-service/Transaction.service';
import PaymentService from '../../service/payment-service/Payment.service';
import { formatCurrency } from '../withdrawal-check/utils';
import { formatCustomerFee } from '../withdrawal-check/utils';
import WithdrawalPaymentModal from '../withdrawal-service/WithdrawalPaymentModal';
import { WithdrawalExportService } from '../../utils/excelExport';
import WithdrawalCardMultiExportModal from './WithdrawalCardMultiExportModal';
import CreateWithdrawalModal from '../withdrawal-service/CreateWithdrawalModal';
import CreateCardWithdrawalModal from './CreateCardWithdrawalModal';
import EditWithdrawalModal from '../withdrawal-service/EditWithdrawalModal';
import PayCustomerModal from '../financial/PayCustomerModal';
import useEmployee from '../../hook/useEmployee';

interface WithdrawalTransactionListProps {
  creditCardId: number;
  onRefresh?: () => void;
  cardInfo?: {
    cardNumber: string;
    cardHolderName: string;
    customerName: string;
    bankName: string;
    cardType: string;
    newStatus?: string;
    defaultFeeRate?: number;
  };
  selectedYear?: number;
  onYearChange?: (year: number) => void;
}

const { Option } = Select;
const { RangePicker } = DatePicker;

const WithdrawalTransactionList: React.FC<WithdrawalTransactionListProps> = ({
  creditCardId,
  onRefresh,
  cardInfo,
  selectedYear,
  onYearChange,
}) => {
  const { employee } = useEmployee();
  const [transactions, setTransactions] = useState<TransactionResponse[]>([]);
  const [filteredTransactions, setFilteredTransactions] = useState<
    TransactionResponse[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<any>(null);
  const [paymentInfoCache, setPaymentInfoCache] = useState<Record<number, any>>(
    {}
  );
  const [showMultiExportModal, setShowMultiExportModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCreateCardModal, setShowCreateCardModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedEditTransaction, setSelectedEditTransaction] =
    useState<TransactionResponse | null>(null);
  const [payCustomerModalVisible, setPayCustomerModalVisible] = useState(false);
  const [selectedTransactionForPay, setSelectedTransactionForPay] =
    useState<TransactionResponse | null>(null);

  // Filter states
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [dateRange, setDateRange] = useState<
    [Dayjs | null, Dayjs | null] | null
  >(null);

  // Use provided selectedYear or default to current year
  const currentYear = selectedYear || new Date().getFullYear();

  useEffect(() => {
    if (creditCardId) {
      loadTransactions();
    }
  }, [creditCardId, currentYear]);

  const loadTransactions = async () => {
    try {
      setLoading(true);
      const response = selectedYear
        ? await TransactionService.getWithdrawalTransactionsByCreditCardAndYear(
            creditCardId,
            currentYear
          )
        : await TransactionService.getWithdrawalTransactionsByCreditCard(
            creditCardId
          );

      if (response.success) {
        const data = response.data || [];
        setTransactions(data);
        setFilteredTransactions(data);

        // Load payment info for all transactions
        await loadPaymentInfo(data);
      }
    } catch (error: any) {
      console.error('Error loading withdrawal transactions:', error);
      // Không hiển thị message error cho việc load dữ liệu ban đầu
    } finally {
      setLoading(false);
    }
  };

  const loadPaymentInfo = async (transactionList: TransactionResponse[]) => {
    const newPaymentInfoCache: Record<number, any> = {};

    // Load payment info for each transaction
    await Promise.all(
      transactionList.map(async (transaction) => {
        try {
          const payments =
            await PaymentService.getPaymentsByWithdrawalTransaction(
              transaction.id
            );

          // Calculate payment summary
          const totalPaid = payments
            .filter(
              (payment: any) =>
                payment.originalStatus === 'COMPLETED' && !payment.deletedAt
            )
            .reduce((total: number, payment: any) => total + payment.amount, 0);

          const paymentCount = payments.filter(
            (payment: any) => !payment.deletedAt
          ).length;

          // Calculate total amount (thành tiền)
          let totalAmount = 0;
          if (transaction.feeForCustomer) {
            if (transaction.customerType === 'vn') {
              // Phí VND: Thành tiền = Phí KH (đã là số tiền cụ thể)
              totalAmount = transaction.feeForCustomer;
            } else {
              // Phí %: Thành tiền = Số tiền rút × (Phí KH %)
              totalAmount =
                transaction.amount * (transaction.feeForCustomer / 100);
            }
          }

          const remainingAmount = totalAmount - totalPaid;

          newPaymentInfoCache[transaction.id] = {
            totalPaid,
            paymentCount,
            remainingAmount,
            totalAmount,
          };
        } catch (error) {
          console.error(
            `Error loading payment info for transaction ${transaction.id}:`,
            error
          );
          newPaymentInfoCache[transaction.id] = {
            totalPaid: 0,
            paymentCount: 0,
            remainingAmount: 0,
            totalAmount: 0,
          };
        }
      })
    );

    setPaymentInfoCache(newPaymentInfoCache);
  };

  const handleDeleteTransaction = async (transactionId: number) => {
    // Find transaction details for warning
    const transaction = transactions.find((t) => t.id === transactionId);
    if (!transaction) {
      message.error('Không tìm thấy giao dịch');
      return;
    }

    const isCompleted = transaction.status === 'COMPLETED';
    const amount = transaction.amount;
    const balance = transaction.balance;

    let warningContent = '';
    if (isCompleted) {
      warningContent = `⚠️ Giao dịch đã HOÀN THÀNH - Sẽ hoàn tác:\n• Tiền nhận từ POS: ${formatCurrency(balance)}`;
    } else {
      warningContent = `⚠️ Giao dịch ĐANG CHỜ - Không có giao dịch tài chính để hoàn tác`;
    }

    Modal.confirm({
      title: 'Xác nhận xóa giao dịch rút tiền',
      content: (
        <div>
          <p>Bạn có chắc chắn muốn xóa giao dịch này?</p>
          <div
            style={{
              background: '#fff7e6',
              border: '1px solid #ffd591',
              borderRadius: '6px',
              padding: '12px',
              marginTop: '12px',
              whiteSpace: 'pre-line',
              fontSize: '13px',
            }}
          >
            {warningContent}
          </div>
          <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#666' }}>
            💡 Hệ thống sẽ tự động:
            <br />• Xóa tất cả thanh toán liên quan và hoàn tiền
            <br />• Điều chỉnh số dư tài khoản tương ứng
          </p>
        </div>
      ),
      okText: 'Xóa giao dịch',
      cancelText: 'Hủy',
      okType: 'danger',
      width: 480,
      onOk: async () => {
        try {
          const response =
            await TransactionService.softDeleteTransaction(transactionId, employee?.id as number);
          if (response.success) {
            message.success('Đã xóa giao dịch và hoàn tác số dư');
            await loadTransactions();
            onRefresh?.();
          }
        } catch (error: any) {
          console.error('Error deleting transaction:', error);
          message.error('Không thể xóa giao dịch');
        }
      },
    });
  };

  const handleToggleStatus = async (transactionId: number) => {
    try {
      const response =
        await TransactionService.toggleWithdrawalStatus(transactionId);
      if (response.success) {
        await loadTransactions();
        onRefresh?.();
      }
    } catch (error: any) {
      console.error('Error toggling transaction status:', error);
      message.error('Không thể cập nhật trạng thái');
    }
  };

  const handleOpenPaymentModal = (transaction: TransactionResponse) => {
    // Convert TransactionResponse to WithdrawalTransaction format
    const withdrawalTransaction = {
      id: transaction.id,
      transactionDate: transaction.transactionDate,
      amount: transaction.amount,
      feeForCustomer: transaction.feeForCustomer,
      customerType: transaction.customerType,
      feeApplied: transaction.feeApplied,
      balance: transaction.balance,
      status: transaction.status,
      returnToCompany: transaction.returnToCompany || false,
      employee: {
        id: transaction.employee?.id || 0,
        name: transaction.employee?.name || '',
        contactInfo: transaction.employee?.contactInfo,
      },
      posDevice: transaction.posDevice
        ? {
            id: transaction.posDevice.id,
            deviceCode: transaction.posDevice.deviceCode,
            location: transaction.posDevice.location,
          }
        : undefined,
      creditCard: transaction.creditCard
        ? {
            id: transaction.creditCard.id,
            cardNumber: transaction.creditCard.cardNumber,
            cardHolderName: transaction.creditCard.cardHolderName,
          }
        : undefined,
    };

    setSelectedTransaction(withdrawalTransaction);
    setPaymentModalVisible(true);
  };

  const handleClosePaymentModal = () => {
    setPaymentModalVisible(false);
    setSelectedTransaction(null);
  };

  const handlePaymentSuccess = async () => {
    await loadTransactions();
    onRefresh?.();
  };

  // Filter functions
  const handleSearch = () => {
    let filtered = [...transactions];

    // Search by text
    if (searchText) {
      filtered = filtered.filter((transaction) => {
        const searchLower = searchText.toLowerCase();
        return (
          transaction.employee?.name?.toLowerCase().includes(searchLower) ||
          transaction.creditCard?.cardNumber?.includes(searchText) ||
          transaction.posDevice?.deviceCode
            ?.toLowerCase()
            .includes(searchLower) ||
          transaction.amount.toString().includes(searchText)
        );
      });
    }

    // Filter by status
    if (statusFilter) {
      filtered = filtered.filter(
        (transaction) => transaction.status === statusFilter
      );
    }

    // Filter by date range
    if (dateRange && dateRange[0] && dateRange[1]) {
      filtered = filtered.filter((transaction) => {
        const transactionDate = new Date(transaction.transactionDate);
        const startDate = dateRange[0]!.toDate();
        const endDate = dateRange[1]!.toDate();
        return transactionDate >= startDate && transactionDate <= endDate;
      });
    }

    setFilteredTransactions(filtered);
  };

  const handleClearFilters = () => {
    setSearchText('');
    setStatusFilter('');
    setDateRange(null);
    setFilteredTransactions(transactions);
  };

  const handleExportExcel = async (transaction: TransactionResponse) => {
    try {
      await WithdrawalExportService.exportToExcel(transaction);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
    }
  };

  const handleMultiExport = () => {
    if (!cardInfo) {
      message.warning('Thiếu thông tin thẻ để xuất Excel');
      return;
    }
    setShowMultiExportModal(true);
  };

  // Filter automatically when filters change
  useEffect(() => {
    handleSearch();
  }, [searchText, statusFilter, dateRange, transactions]);

  // Handle year change
  const handleYearChange = (year: dayjs.Dayjs | null) => {
    if (year && onYearChange) {
      onYearChange(year.year());
    }
  };

  // Check if card is disabled
  const isCardDisabled = cardInfo?.newStatus === 'KHONG_LAM';

  const handleCreateSuccess = () => {
    loadTransactions();
    onRefresh?.();
    setShowCreateModal(false);
  };

  const handleCreateCardSuccess = () => {
    setShowCreateCardModal(false);
    loadTransactions();
    onRefresh?.();
  };

  const handleEditTransaction = (transaction: TransactionResponse) => {
    setSelectedEditTransaction(transaction);
    setShowEditModal(true);
  };

  const handleEditSuccess = () => {
    loadTransactions();
    onRefresh?.();
    setShowEditModal(false);
    setSelectedEditTransaction(null);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedEditTransaction(null);
  };

  const handlePayCustomer = (transaction: TransactionResponse) => {
    setSelectedTransactionForPay(transaction);
    setPayCustomerModalVisible(true);
  };

  const handlePayCustomerModalClose = () => {
    setPayCustomerModalVisible(false);
    setSelectedTransactionForPay(null);
  };

  const handlePayCustomerSuccess = () => {
    handlePayCustomerModalClose();
    loadTransactions(); // Reload to show updated data
    onRefresh?.();
  };

  const columns = [
    {
      title: 'Ngày GD',
      dataIndex: 'transactionDate',
      key: 'transactionDate',
      width: 100,
      render: (date: string) => {
        const dateObj = new Date(date);
        return dateObj.toLocaleDateString('vi-VN');
      },
      sorter: (a: TransactionResponse, b: TransactionResponse) =>
        new Date(a.transactionDate).getTime() -
        new Date(b.transactionDate).getTime(),
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: any) => (
        <div style={{ padding: 8 }}>
          <RangePicker
            value={selectedKeys[0] as any}
            onChange={(dates) => setSelectedKeys(dates ? [dates as any] : [])}
            style={{ width: 300, marginBottom: 8, display: 'block' }}
            format="DD/MM/YYYY"
          />
          <Space>
            <Button
              type="primary"
              onClick={() => confirm()}
              size="small"
              style={{ width: 90 }}
            >
              Lọc
            </Button>
            <Button
              onClick={() => clearFilters?.()}
              size="small"
              style={{ width: 90 }}
            >
              Xóa
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <FilterOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
      onFilter: (value: any, record: TransactionResponse) => {
        if (!value || !value[0] || !value[1]) return true;
        const recordDate = new Date(record.transactionDate);
        const startDate = value[0].toDate();
        const endDate = value[1].toDate();
        return recordDate >= startDate && recordDate <= endDate;
      },
    },
    {
      title: 'Số tiền',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      render: (amount: number) => (
        <span className="font-medium text-blue-600">
          {formatCurrency(amount)}
        </span>
      ),
      sorter: (a: TransactionResponse, b: TransactionResponse) =>
        a.amount - b.amount,
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: any) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Tìm số tiền"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => confirm()}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => confirm()}
              size="small"
              style={{ width: 90 }}
            >
              Tìm
            </Button>
            <Button
              onClick={() => clearFilters?.()}
              size="small"
              style={{ width: 90 }}
            >
              Xóa
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
      onFilter: (value: any, record: TransactionResponse) =>
        record.amount.toString().includes(value as string),
    },
    {
      title: 'Phí KH',
      dataIndex: 'feeForCustomer',
      key: 'feeForCustomer',
      width: 120,
      render: (fee: number, record: TransactionResponse) => {
        if (!fee || fee === 0) {
          return <span className="text-gray-400">0</span>;
        }

        if (record.customerType === 'vn') {
          return (
            <span className="text-blue-500 font-medium text-xs">
              {formatCurrency(fee)}
            </span>
          );
        } else {
          return (
            <span className="text-orange-500 font-medium text-xs">
              {fee.toFixed(2)}%
            </span>
          );
        }
      },
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: any) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Tìm phí khách hàng"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => confirm()}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => confirm()}
              size="small"
              style={{ width: 90 }}
            >
              Tìm
            </Button>
            <Button
              onClick={() => clearFilters?.()}
              size="small"
              style={{ width: 90 }}
            >
              Xóa
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
      onFilter: (value: any, record: TransactionResponse) => {
        if (!value) return true;
        const searchText = (value as string).toLowerCase();
        const { feeForCustomer, customerType } = record;

        // Search by fee value
        if (feeForCustomer && feeForCustomer.toString().includes(searchText))
          return true;

        // Search by type
        if (customerType === 'vn' && 'vnd'.includes(searchText)) return true;
        if (
          customerType === '%' &&
          ('phần trăm'.includes(searchText) || '%'.includes(searchText))
        )
          return true;

        // Search by formatted value
        if (customerType === 'vn') {
          const formatted = formatCurrency(feeForCustomer || 0).toLowerCase();
          if (formatted.includes(searchText)) return true;
        } else {
          const formatted = `${feeForCustomer?.toFixed(2) || 0}%`;
          if (formatted.toLowerCase().includes(searchText)) return true;
        }

        return false;
      },
      filters: [
        { text: 'VND', value: 'vn' },
        { text: 'Phần trăm (%)', value: '%' },
        { text: 'Không có phí', value: 'none' },
      ],
    },
    {
      title: 'Phí POS (%)',
      dataIndex: 'feeApplied',
      key: 'feeApplied',
      width: 80,
      render: (fee: number) => (fee ? `${fee}%` : '0%'),
      sorter: (a: TransactionResponse, b: TransactionResponse) =>
        (a.feeApplied || 0) - (b.feeApplied || 0),
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: any) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Tìm phí POS"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => confirm()}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => confirm()}
              size="small"
              style={{ width: 90 }}
            >
              Tìm
            </Button>
            <Button
              onClick={() => clearFilters?.()}
              size="small"
              style={{ width: 90 }}
            >
              Xóa
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
      onFilter: (value: any, record: TransactionResponse) =>
        record.feeApplied?.toString().includes(value as string) || false,
    },
    {
      title: 'Phí CTV (%)',
      dataIndex: 'commissionRateForPartner',
      key: 'commissionRateForPartner',
      width: 80,
      render: (commission: number) => (
        <span className="text-purple-500 text-xs">
          {commission ? `${commission}%` : 'N/A'}
        </span>
      ),
      sorter: (a: TransactionResponse, b: TransactionResponse) =>
        (a.commissionRateForPartner || 0) - (b.commissionRateForPartner || 0),
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: any) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Tìm phí CTV"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => confirm()}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => confirm()}
              size="small"
              style={{ width: 90 }}
            >
              Tìm
            </Button>
            <Button
              onClick={() => clearFilters?.()}
              size="small"
              style={{ width: 90 }}
            >
              Xóa
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
      onFilter: (value: any, record: TransactionResponse) =>
        record.commissionRateForPartner?.toString().includes(value as string) ||
        false,
    },
    {
      title: 'Thành tiền',
      key: 'totalAmount',
      width: 120,
      render: (record: TransactionResponse) => {
        const { amount, feeForCustomer, customerType } = record;
        let totalAmount = 0;

        if (feeForCustomer) {
          if (customerType === 'vn') {
            // Phí VND: Thành tiền = Phí KH (đã là số tiền cụ thể)
            totalAmount = feeForCustomer;
          } else {
            // Phí %: Thành tiền = Số tiền rút × (Phí KH %)
            totalAmount = amount * (feeForCustomer / 100);
          }
        }

        return (
          <span className="font-medium text-purple-600 text-xs">
            {formatCurrency(totalAmount)}
          </span>
        );
      },
      sorter: (a: TransactionResponse, b: TransactionResponse) => {
        const getTotalAmount = (record: TransactionResponse) => {
          const { amount, feeForCustomer, customerType } = record;
          if (!feeForCustomer) return 0;
          return customerType === 'vn'
            ? feeForCustomer
            : amount * (feeForCustomer / 100);
        };
        return getTotalAmount(a) - getTotalAmount(b);
      },
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: any) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Tìm thành tiền"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => confirm()}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => confirm()}
              size="small"
              style={{ width: 90 }}
            >
              Tìm
            </Button>
            <Button
              onClick={() => clearFilters?.()}
              size="small"
              style={{ width: 90 }}
            >
              Xóa
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
      onFilter: (value: any, record: TransactionResponse) => {
        const { amount, feeForCustomer, customerType } = record;
        let totalAmount = 0;
        if (feeForCustomer) {
          totalAmount =
            customerType === 'vn'
              ? feeForCustomer
              : amount * (feeForCustomer / 100);
        }
        return totalAmount.toString().includes(value as string);
      },
    },

    {
      title: 'Thiết bị POS',
      key: 'posDevice',
      width: 150,
      render: (record: TransactionResponse) => {
        if (!record.posDevice) return '-';
        return (
          <div className="text-xs">
            <div className="font-medium">{record.posDevice.deviceCode}</div>
            <div className="text-gray-500">{record.posDevice.location}</div>
          </div>
        );
      },
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: any) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Tìm POS/địa điểm"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => confirm()}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => confirm()}
              size="small"
              style={{ width: 90 }}
            >
              Tìm
            </Button>
            <Button
              onClick={() => clearFilters?.()}
              size="small"
              style={{ width: 90 }}
            >
              Xóa
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
      onFilter: (value: any, record: TransactionResponse) => {
        const searchText = (value as string).toLowerCase();
        return (
          record.posDevice?.deviceCode?.toLowerCase().includes(searchText) ||
          record.posDevice?.location?.toLowerCase().includes(searchText) ||
          false
        );
      },
    },
    {
      title: 'Nhân viên',
      key: 'employee',
      width: 120,
      render: (record: TransactionResponse) => {
        if (!record.employee) return '-';
        return (
          <div className="text-xs">
            <div className="font-medium">{record.employee.name}</div>
            <div className="text-gray-500">{record.employee.contactInfo}</div>
          </div>
        );
      },
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: any) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Tìm nhân viên"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => confirm()}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => confirm()}
              size="small"
              style={{ width: 90 }}
            >
              Tìm
            </Button>
            <Button
              onClick={() => clearFilters?.()}
              size="small"
              style={{ width: 90 }}
            >
              Xóa
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
      onFilter: (value: any, record: TransactionResponse) =>
        record.employee?.name
          ?.toLowerCase()
          .includes((value as string).toLowerCase()) || false,
    },
    {
      title: 'Thanh toán',
      key: 'payment',
      width: 120,
      render: (record: TransactionResponse) => {
        const paymentInfo = paymentInfoCache[record.id];

        if (!paymentInfo) {
          return <div className="text-xs text-gray-400">Đang tải...</div>;
        }

        const { totalPaid, paymentCount, remainingAmount, totalAmount } =
          paymentInfo;

        return (
          <div className="text-xs">
            <div className="text-green-600 font-medium">
              {formatCurrency(totalPaid)}
            </div>
            <div className="text-gray-500">{paymentCount} lần</div>
            {remainingAmount > 0 && (
              <div className="text-orange-500 text-xs">
                Còn: {formatCurrency(remainingAmount)}
              </div>
            )}
            {remainingAmount < 0 && (
              <div className="text-blue-500 text-xs">
                Thừa: {formatCurrency(Math.abs(remainingAmount))}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Tiền về',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => (
        <Tag color={status === 'COMPLETED' ? 'green' : 'orange'}>
          {status === 'COMPLETED' ? 'Hoàn thành' : 'Đang chờ'}
        </Tag>
      ),
      filters: [
        { text: 'Hoàn thành', value: 'COMPLETED' },
        { text: 'Đang chờ', value: 'PENDING' },
      ],
      onFilter: (value: any, record: TransactionResponse) =>
        record.status === value,
    },
    {
      title: 'Hành động',
      key: 'actions',
      width: 180,
      fixed: 'right' as const,
      render: (record: TransactionResponse) => (
        <Space>
          <Button
            type="text"
            size="small"
            icon={<DollarOutlined />}
            onClick={() => handleOpenPaymentModal(record)}
            title="Ghi nhận thanh toán"
          />
          <Button
            type="text"
            size="small"
            icon={<TransactionOutlined />}
            onClick={() => handlePayCustomer(record)}
            title="Trả cho khách"
            className="text-orange-500 hover:bg-orange-50"
          />
          <Button
            type="text"
            size="small"
            icon={<FileExcelOutlined />}
            onClick={() => handleExportExcel(record)}
            title="Xuất hóa đơn Excel"
            className="text-green-500 hover:bg-green-50"
          />
          <Button
            type="text"
            size="small"
            icon={<SyncOutlined />}
            onClick={() => handleToggleStatus(record.id)}
            title={
              record.status === 'COMPLETED'
                ? 'Chuyển về Đang chờ'
                : 'Chuyển về Hoàn thành'
            }
          />
          <Dropdown
            menu={{
              items: [
                {
                  key: 'edit',
                  icon: <EditOutlined />,
                  label: 'Sửa giao dịch',
                  onClick: () => handleEditTransaction(record),
                },
                {
                  key: 'delete',
                  icon: <DeleteOutlined />,
                  label: 'Xóa giao dịch',
                  danger: true,
                  onClick: () => {
                    // Handle delete with confirm
                    if (
                      window.confirm(
                        'Bạn có chắc chắn muốn xóa giao dịch này không?'
                      )
                    ) {
                      handleDeleteTransaction(record.id);
                    }
                  },
                },
              ],
            }}
            trigger={['click']}
          >
            <Button
              type="text"
              size="small"
              icon={<MoreOutlined />}
              title="Thêm thao tác"
            />
          </Dropdown>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* Header with year picker */}
      <Card size="small" className="mb-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <CalendarOutlined className="text-blue-500" />
            <span className="text-sm font-medium">Chọn năm:</span>
            <DatePicker
              picker="year"
              value={selectedYear ? dayjs().year(selectedYear) : undefined}
              onChange={handleYearChange}
              format="YYYY"
              allowClear={false}
              className="w-32"
            />
          </div>
          <div className="flex items-center space-x-2">
            <Button
              type="primary"
              icon={<SyncOutlined />}
              onClick={loadTransactions}
              loading={loading}
              size="small"
            >
              Tải lại
            </Button>
          </div>
        </div>
      </Card>

      <div className="mb-4 flex justify-between items-center">
        <div>
          <h4 className="text-lg font-medium mb-2">Giao dịch rút tiền</h4>
          <p className="text-gray-600 text-sm">
            Danh sách các giao dịch rút tiền của thẻ này theo năm {currentYear}.
          </p>
        </div>
        <Space>
          <Tooltip
            title={
              isCardDisabled
                ? 'Thẻ không hoạt động - không thể tạo giao dịch mới'
                : 'Tạo giao dịch rút tiền mới'
            }
          >
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setShowCreateCardModal(true)}
              disabled={isCardDisabled}
              className={isCardDisabled ? 'opacity-50 cursor-not-allowed' : ''}
            >
              Tạo GD mới
            </Button>
          </Tooltip>
          <Button
            type="default"
            icon={<FileExcelOutlined />}
            onClick={handleMultiExport}
            className="bg-green-600 hover:bg-green-700 text-white border-green-600 hover:border-green-700"
            disabled={!cardInfo || filteredTransactions.length === 0}
          >
            Xuất Excel nhiều GD
          </Button>
        </Space>
      </div>

      {/* Filters */}
      <Card size="small" className="mb-4">
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8}>
            <Input
              placeholder="Tìm kiếm theo tên, thẻ, POS..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              size="small"
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Trạng thái"
              value={statusFilter}
              onChange={setStatusFilter}
              size="small"
              style={{ width: '100%' }}
              allowClear
            >
              <Option value="COMPLETED">Hoàn thành</Option>
              <Option value="PENDING">Đang chờ</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <RangePicker
              placeholder={['Từ ngày', 'Đến ngày']}
              value={dateRange}
              onChange={setDateRange}
              size="small"
              style={{ width: '100%' }}
              format="DD/MM/YYYY"
            />
          </Col>
          <Col xs={24} sm={12} md={2}>
            <Button
              icon={<ClearOutlined />}
              onClick={handleClearFilters}
              size="small"
              title="Xóa bộ lọc"
            />
          </Col>
        </Row>
      </Card>

      <Table
        columns={columns}
        dataSource={filteredTransactions}
        rowKey="id"
        loading={loading}
        pagination={{
          pageSize: 20,
          showSizeChanger: false,
          showQuickJumper: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} của ${total} giao dịch (Tổng: ${transactions.length})`,
        }}
        scroll={{ x: 1470 }}
        size="small"
        bordered
      />

      {/* Payment Modal */}
      <WithdrawalPaymentModal
        visible={paymentModalVisible}
        onCancel={handleClosePaymentModal}
        onSuccess={handlePaymentSuccess}
        transaction={selectedTransaction}
      />

      {/* Create Withdrawal Modal */}
      <CreateWithdrawalModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleCreateSuccess}
      />

      {/* Multi Export Modal */}
      {cardInfo && (
        <WithdrawalCardMultiExportModal
          visible={showMultiExportModal}
          onClose={() => setShowMultiExportModal(false)}
          transactions={filteredTransactions}
          cardInfo={cardInfo}
          selectedYear={currentYear}
        />
      )}

      {/* Create Card Withdrawal Modal */}
      {cardInfo && (
        <CreateCardWithdrawalModal
          open={showCreateCardModal}
          onClose={() => setShowCreateCardModal(false)}
          onSuccess={handleCreateCardSuccess}
          creditCardId={creditCardId}
          cardInfo={{
            cardNumber: cardInfo.cardNumber,
            cardHolderName: cardInfo.cardHolderName,
            customerName: cardInfo.customerName,
            bankName: cardInfo.bankName,
            cardType: cardInfo.cardType,
            defaultFeeRate: cardInfo.defaultFeeRate,
          }}
        />
      )}

      {/* Edit Withdrawal Modal */}
      {selectedEditTransaction && (
        <EditWithdrawalModal
          open={showEditModal}
          onClose={handleCloseEditModal}
          onSuccess={handleEditSuccess}
          transaction={selectedEditTransaction}
        />
      )}

      {/* Pay Customer Modal */}
      {selectedTransactionForPay && (
        <PayCustomerModal
          visible={payCustomerModalVisible}
          onCancel={handlePayCustomerModalClose}
          onSuccess={handlePayCustomerSuccess}
          transaction={{
            id: selectedTransactionForPay.id,
            amount: selectedTransactionForPay.amount,
            balance: selectedTransactionForPay.balance,
            creditCard: selectedTransactionForPay.creditCard,
            transactionDate: selectedTransactionForPay.transactionDate,
            feeForCustomer: selectedTransactionForPay.feeForCustomer,
            customerType: selectedTransactionForPay.customerType,
          }}
        />
      )}
    </div>
  );
};

export default WithdrawalTransactionList;
